import { reaction, runInAction } from "mobx";
import * as Sentry from "@sentry/react";
import ClienteEnvelopeApi from "../api/ClienteEnvelopeApi";
import { CategoriaMinuta, ControleGabinete, IEtiqueta, ISearch, Minuta, MinutaCategoriaEnvelope, Processo, TipoMinuta } from "../modelo";
import { Comentario } from "../modelo/Comentario";
import { EnvelopeResposta } from "../modelo/Envelope";
import { MinutaMovimentos } from "../modelo/MinutaMovimentos";
import { ProcessoImportacao } from "../modelo/ProcessoImportacao";
import { ITarefaFluxoPje } from "../modelo/TarefaFluxoPje";
import { TipoJulgamentoEnum } from "../modelo/TipoJulgamento";
import { DadosProcessoDto } from "../modelo/dto/DadosProcessoDto";
import UsuarioStore from '../store/UsuarioStore';
import ControleGabineteStore from '../store/ControleGabineteStore';
import FiltroStore from "../store/FiltroStore";
import ImportacaoProcessosStore from "../store/ImportacaoProcessosStore";
import ProcessoStore from "../store/ProcessoStore";
import RouterStore from "../store/RouterStore";
import ControleGabineteService from "./ControleGabineteService";
import EscaninhoService from "./EscaninhoService";
import ImportacaoService from "./ImportacaoService";
import InformacoesProcessoService from "./InformacoesProcessoService";
import MapeadorProcesso from "./mapeadores/MapeadorProcesso";
import ProcessoService from "./ProcessoService";
import { SituacaoMinuta } from "@/modelo/SituacaoMinuta";
import GabineteStore from "@/store/GabineteStore";
import ProcessoExpandidoStore from "@/store/ProcessoExpandidoStore";

// Payloads indexados por id do processo (idProcesso -> informação), mesclados durante a janela.
type LoteMensagens = Record<string, any>;

interface ILotesPendentes {
    tarefaEResponsavel: LoteMensagens;
    tarefaProcesso: LoteMensagens;
    etiquetas: LoteMensagens;
}

class ClienteEnvelopeService {

    fila;
    timerAtivo;

    // Mensagens que só alteram campos de processos já conhecidos chegam em rajadas (ex.: movimentação
    // em massa). Aplicar uma a uma faz a tela reprocessar tudo a cada mensagem; aqui elas são mescladas
    // por processo (a última vence) e aplicadas de uma vez a cada janela.
    private readonly janelaLoteMs = 1500;
    private readonly tamanhoLoteBuscaProcessos = 100;
    private lotesPendentes: ILotesPendentes = this.novosLotesPendentes();
    private timerLote?: ReturnType<typeof setTimeout>;
    private processandoLote = false;
    // Ids com atualização na fila (sem repetição), para o aviso "N atualizações pendentes".
    private idsPendentes = new Set<string>();

    constructor() {
        this.fila = [];
        this.timerAtivo = false;

        // Quando o usuário para de interagir com o Kanban, aplica na hora o que ficou retido.
        reaction(
            () => ControleGabineteStore.atualizacoesPausadas,
            (pausada) => {
                if (!pausada) {
                    this.processarLote();
                }
            }
        );
    }

    private novosLotesPendentes(): ILotesPendentes {
        return { tarefaEResponsavel: {}, tarefaProcesso: {}, etiquetas: {} };
    }

    private enfileirarNoLote(tipo: keyof ILotesPendentes, informacoes: LoteMensagens) {
        if (!informacoes) {
            return;
        }
        Object.assign(this.lotesPendentes[tipo], informacoes);
        Object.keys(informacoes).forEach(idProcesso => this.idsPendentes.add(idProcesso));
        this.agendarLote();
    }

    private temLotePendente() {
        return Object.values(this.lotesPendentes).some(lote => Object.keys(lote).length > 0);
    }

    private agendarLote() {
        if (this.timerLote) {
            return;
        }
        this.timerLote = setTimeout(() => {
            this.timerLote = undefined;
            this.processarLote();
        }, this.janelaLoteMs);
    }

    private async processarLote() {
        // Um lote por vez: o seguinte só começa quando o atual termina (evita duplicar processos buscados).
        if (this.processandoLote) {
            return;
        }

        if (!this.temLotePendente()) {
            // Ex.: os processos pendentes foram removidos enquanto retidos.
            ControleGabineteStore.setProcessosComAtualizacaoPendente(0);
            return;
        }

        // Usuário mexendo no Kanban: retém o lote e só informa quantas atualizações estão esperando.
        // O timer continua rearmado para manter a contagem em dia; ao retomar, a reaction do construtor aplica.
        if (ControleGabineteStore.atualizacoesPausadas) {
            ControleGabineteStore.setProcessosComAtualizacaoPendente(this.idsPendentes.size);
            this.agendarLote();
            return;
        }

        this.processandoLote = true;

        try {
            const { tarefaEResponsavel, tarefaProcesso, etiquetas } = this.lotesPendentes;
            this.lotesPendentes = this.novosLotesPendentes();
            this.idsPendentes.clear();
            ControleGabineteStore.setProcessosComAtualizacaoPendente(0);

            await this.executarSemPropagarErro('tarefa e responsável', () => this.atualizarTarefaEResponsavel(tarefaEResponsavel));
            await this.executarSemPropagarErro('tarefa do processo', async () => this.atualizarTarefaProcesso(tarefaProcesso));
            await this.executarSemPropagarErro('etiquetas', async () => this.atualizarEtiquetas(etiquetas));
        } finally {
            this.processandoLote = false;
            if (this.temLotePendente()) {
                this.agendarLote();
            }
        }
    }

    private async executarSemPropagarErro(descricao: string, tarefa: () => Promise<void>) {
        try {
            await tarefa();
        } catch (erro) {
            console.warn(`Erro ao aplicar lote de ${descricao}`, erro);
            Sentry.captureException(erro, { level: 'warning' });
        }
    }

    private indexarProcessosPorId(): Map<number, Processo> {
        const indice = new Map<number, Processo>();
        ProcessoStore._processos.forEach(processo => {
            if (!indice.has(processo.identificadorProcesso)) {
                indice.set(processo.identificadorProcesso, processo);
            }
        });
        return indice;
    }

    private async buscarProcessosDesconhecidos(ids: number[]): Promise<Map<number, Processo>> {
        const encontrados = new Map<number, Processo>();
        const searchGabinete: ISearch = {
            gabinete: UsuarioStore.gabinete
        };

        for (let i = 0; i < ids.length; i += this.tamanhoLoteBuscaProcessos) {
            const lote = ids.slice(i, i + this.tamanhoLoteBuscaProcessos);
            try {
                const processos = await ProcessoService.processoPorIdsFiltroRapido(UsuarioStore.gabinete, lote, searchGabinete);
                (processos || []).forEach(dto => {
                    const processo = Processo.novo(dto);
                    encontrados.set(processo.identificadorProcesso, processo);
                });
            } catch (erro) {
                console.warn('Erro ao buscar processos novos recebidos via envelope', lote);
                Sentry.captureException(erro, { level: 'warning' });
            }
        }

        return encontrados;
    }

    acompanharGabinete() {
        ClienteEnvelopeApi.acompanharGabinete(UsuarioStore.gabinete, (mensagem) => {
            try {
                // console.info('recebida mensagem', mensagem.data);

                const envelopeResposta: EnvelopeResposta = JSON.parse(mensagem.data);
                // console.info('recebida mensagem', envelopeResposta);

                runInAction(() => {
                    GabineteStore.ultimaAtualizacaoEnvelope = new Date();
                });

                switch (envelopeResposta.acao) {
                    case 'IMPORTACAO':

                        if (envelopeResposta.resposta.erro) {
                            this.importacaoErro(envelopeResposta).catch(erro => {
                                console.warn('Erro ao tratar importação com erro', mensagem.data);
                                Sentry.captureException(erro, { level: 'warning' });
                            });
                        } else {
                            this.fila.push(envelopeResposta);

                            if (!this.timerAtivo) {
                                this.timerAtivo = true;

                                setTimeout(async () => {
                                    try {
                                        while (this.fila.length > 0) {
                                            const fileTemp = this.fila;
                                            this.fila = [];
                                            await this.importacaoSucesso(fileTemp);
                                        }
                                    } finally {
                                        this.timerAtivo = false;
                                    }
                                }, 5000);
                            }
                        }

                        break;
                    case 'ALTERACAO_MINUTA':
                        this.atualizarMinuta(envelopeResposta.resposta);
                        break;
                    case 'ALTERACAO_CONTROLE_GABINETE':
                        this.atualizarControleGabinete(envelopeResposta.resposta);
                        break;
                    case 'MINUTA_ARQUIVADA':
                    case 'MINUTA_FOI_ASSINADA':
                        this.minutaArquivada(envelopeResposta.resposta);
                        break;
                    case 'COMENTARIO_MINUTA_DELETADO':
                        this.remocaoComentario(envelopeResposta.resposta);
                        break;
                    case 'ALTERACAO_MINUTAS':
                        this.alteracaoMinutas(envelopeResposta.resposta);
                        break;
                    case 'ALTERACAO_COMENTARIO':
                        this.alteracaoComentario(envelopeResposta.resposta);
                        break;
                    case 'NOVO_COMENTARIO':
                        this.novoComentario(envelopeResposta.resposta);
                        break;
                    case 'ALTERACAO_TIPO_JULGAMENTO':
                        this.alteracaoTipoJulgamento(envelopeResposta.resposta);
                        break;
                    case 'VOTOS_LIBERADOS':
                        this.votosLiberados(envelopeResposta.resposta);
                        break;
                    case 'ALTERACAO_TRIAGEM_LIVRE':
                        this.alteracaoTriagemLivre(envelopeResposta.resposta);
                        break;
                    case 'NOVA_MINUTA':
                        this.alteracaoNovaMinuta(envelopeResposta.resposta);
                        break;
                    case 'ALTERCAO_MINUTA_MOVIMENTO':
                        this.alteracaoMinutaMovimento(envelopeResposta.resposta);
                        break;
                    case 'ALTERACAO_PROCESSO':
                        this.atualizarProcesso(envelopeResposta.resposta);
                        break;
                    case 'ALTERACAO_TAREFA_RESPONSAVEL':
                        this.enfileirarNoLote('tarefaEResponsavel', envelopeResposta.resposta);
                        break;
                    case 'ALTERACAO_ETIQUETA':
                        this.enfileirarNoLote('etiquetas', envelopeResposta.resposta);
                        break;
                    case 'ALTERACAO_TAREFA_PROCESSO':
                        this.enfileirarNoLote('tarefaProcesso', envelopeResposta.resposta);
                        break;
                    case 'ALTERACAO_CATEGORIA_MINUTA':
                        this.atualizarCategoriaMinuta(envelopeResposta.resposta);
                        break;
                    case 'REMOCAO_PROCESSO':
                        this.removerProcesso(envelopeResposta.resposta);
                        break;
                    case 'ATUALIZACAO_PROCESSO':
                        envelopeResposta.resposta.forEach(idProcesso => {
                            EscaninhoService.atualizarProcesso(idProcesso, true).catch(erro => {
                                console.warn('Erro ao atualizar processo via envelope', idProcesso);
                                Sentry.captureException(erro, { level: 'warning' });
                            });
                        });
                        break;
                    case 'MINUTA_ABERTA':
                        // envelopeResposta.resposta.forEach(idProcesso => {
                        //     EscaninhoService.atualizarProcesso(idProcesso, true);
                        // });
                        break;
                    case 'ERRO_MINUTA':
                        this.fechamentoDeMinuta(envelopeResposta.resposta);
                        break;
                    case 'CONTEM_TRECHO_PUBLIQUESE':
                        this.atualizarContemTrechoPubliquese(envelopeResposta.resposta);
                        break;
                    case 'CONTEM_TRECHO_APOS_PAUTA':
                        this.atualizarContemTrechoAposPauta(envelopeResposta.resposta);
                        break;
                    default:
                        break;
                }
            } catch (erro) {
                console.warn('Erro ao tratar a mensagem', mensagem.data);
                Sentry.captureException(erro, {
                    level: 'warning'
                });
            }
        });
    }

    async importacaoErro(envelopeResposta) {
        if (envelopeResposta.resposta.mensagem.includes('Erro ao buscar informações do processo na origem')) {
            // verificar se o processo foi importado e o erro foi ao consultar outras informações do processo
            if (RouterStore.isImportacaoNoEscaninho) {
                const searchGabinete: ISearch = {
                    gabinete: UsuarioStore.gabinete
                };
                const idProcesso = await ProcessoService.idProcessoPorNumero(envelopeResposta.resposta.numero);
                if (idProcesso) {
                    const processoJaImportado = await ProcessoService.processoPorIdFiltroRapido(UsuarioStore.gabinete, idProcesso, searchGabinete);
                    const processo = Processo.novo(processoJaImportado[0]);
                    if (processo?.numeroProcesso) {
                        runInAction(() => {
                            if (FiltroStore.filtroAplicado) {
                                FiltroStore.aplicarFiltros(processo);
                            }
                            ProcessoStore._processos.push(processo);
                        });
                        await EscaninhoService.atualizarProcessoNaStore(processo, true);
                    }
                }
                envelopeResposta.resposta.erro = false;
                this.adicionarProcessoNoEscaninhoDeImportacao(envelopeResposta.resposta, envelopeResposta.resposta.numero);
            }
        } else {
            this.adicionarProcessoNoEscaninhoDeImportacao(envelopeResposta.resposta, envelopeResposta.resposta.numero);
        }
    }

    async importacaoSucesso(envelopesRespostas) {
        const _processosForaDoGE = ProcessoStore._processosForaDoGE;

        for (const envelopeResposta of envelopesRespostas) {
            for (const processoImportado of envelopeResposta.resposta.processosImportados) {
                const proc = MapeadorProcesso.toProcesso(processoImportado);
                const procJaExiste = ProcessoStore._processos.find(procStore => proc.identificadorProcesso === procStore.identificadorProcesso);
                let dadosProcesso;

                try {
                    dadosProcesso = await InformacoesProcessoService.consultarProcesso(
                        proc.identificadorProcesso,
                        false,
                        { carregarPecas: false }
                    );
                    DadosProcessoDto.toProcesso(dadosProcesso, proc);
                    proc.relatorOriginal = undefined;
                } catch (erro) {
                    console.warn(`Não foi possível recuperar os dados completos do processo importado ${proc.numeroProcesso}.`, erro);
                    try {
                        proc.tarefaFluxoPje = await InformacoesProcessoService.consultarTarefaFluxoPJe(proc.identificadorProcesso) as any as ITarefaFluxoPje;
                    } catch (erroTarefa) {
                        console.warn(`Não foi possível recuperar a tarefa do processo importado ${proc.numeroProcesso}.`, erroTarefa);
                    }
                }

                runInAction(() => {
                    if (procJaExiste && dadosProcesso) {
                        DadosProcessoDto.toProcesso(dadosProcesso, procJaExiste);
                        procJaExiste.relatorOriginal = undefined;
                    } else if (procJaExiste && proc.tarefaFluxoPje) {
                        procJaExiste.tarefaFluxoPje = proc.tarefaFluxoPje;
                    }

                    if (procJaExiste) {
                        procJaExiste.resetMostrar();
                        FiltroStore.aplicarFiltros(procJaExiste);
                    } else {
                        FiltroStore.aplicarFiltros(proc);
                        ProcessoStore._processos.push(proc);
                    }
                });
                const index = ProcessoStore._processosForaDoGE.findIndex(procFG => procFG.identificadorProcesso === proc.identificadorProcesso);
                if (index >= 0) {
                    runInAction(() => {
                        _processosForaDoGE.splice(index, 1);
                    });
                }

                if (RouterStore.isImportacaoNoEscaninho) {
                    const codImportacao = RouterStore.getParam('codImportacao');
                    if (codImportacao !== undefined && codImportacao[0].substring(0, codImportacao[0].lastIndexOf('-')) === envelopeResposta.resposta.uuidImportacao) {
                        ImportacaoService.iniciaEventoImportacao(codImportacao[0]);
                        const processoImportacao = new ProcessoImportacao(envelopeResposta.resposta.mensagem, envelopeResposta.resposta.erro, proc.numeroProcesso);
                        runInAction(() => {
                            ImportacaoProcessosStore.adicionarProcessoNoEscaninho(processoImportacao);
                            ImportacaoProcessosStore.adicionarProcessoimportado(processoImportacao);
                        });
                    }
                }
            }
        }

        runInAction(() => {
            ProcessoStore._processosForaDoGE = _processosForaDoGE;
        });
    }

    adicionarProcessoNoEscaninhoDeImportacao = (resposta, numeroProcesso: string) => {
        if (RouterStore.isImportacaoNoEscaninho) {
            const codImportacao = RouterStore.getParam('codImportacao');
            if (codImportacao !== undefined && codImportacao[0].substring(0, codImportacao[0].lastIndexOf('-')) === resposta.uuidImportacao) {
                ImportacaoService.iniciaEventoImportacao(codImportacao[0]);
                const processoImportacao = new ProcessoImportacao(resposta.mensagem, resposta.erro, numeroProcesso);
                runInAction(() => { // todo: ver se tira
                    ImportacaoProcessosStore.adicionarProcessoNoEscaninho(processoImportacao);
                    ImportacaoProcessosStore.adicionarProcessoimportado(processoImportacao);
                });
            }
        }
    }

    atualizarProcesso(processosAlterados: Processo[]) {
        const processos = MapeadorProcesso.toProcessos(processosAlterados);
        processos.map(processoAlterado => {
            const proc = Processo.novo(processoAlterado);
            EscaninhoService.atualizarProcessoNaStore(proc, false);
        });
    }

    async atualizarTarefaEResponsavel(informacoes: LoteMensagens) {
        const ids = Object.keys(informacoes).map(idProc => parseInt(idProc));
        if (ids.length === 0) {
            return;
        }

        const idsConhecidos = this.indexarProcessosPorId();
        const idsDesconhecidos = ids.filter(id => !idsConhecidos.has(id));
        const processosNovos = idsDesconhecidos.length > 0
            ? await this.buscarProcessosDesconhecidos(idsDesconhecidos)
            : new Map<number, Processo>();

        // Uma única action para o lote todo: as reactions do MobX rodam uma vez só, no final.
        runInAction(() => {
            // Reindexa: a store pode ter mudado enquanto os processos novos eram buscados.
            const processosPorId = this.indexarProcessosPorId();

            ids.forEach(idProcesso => {
                try {
                    const infoProcesso = informacoes[idProcesso];
                    let processo = processosPorId.get(idProcesso);
                    if (!processo) {
                        processo = processosNovos.get(idProcesso);
                        if (!processo) {
                            return;
                        }
                        ProcessoStore._processos.push(processo);
                        processosPorId.set(idProcesso, processo);
                    }

                    processo.controleGabinete = infoProcesso.controleGabinete as any as ControleGabinete;
                    processo.numeroCpfResponsavel = infoProcesso.numeroCpfResponsavel;
                    // processo.minutaParaAssinarDto = infoProcesso.minutaParaAssinarDto;
                    processo.resetMostrar();
                    FiltroStore.aplicarFiltros(processo);
                    if (!processo.mostrar) {
                        //processo.deselecionarMinutas();
                        processo.selecionar(false);
                    }
                } catch (erro) {
                    console.warn('Erro ao atualizar tarefa e responsável do processo', idProcesso);
                    Sentry.captureException(erro, { level: 'warning' });
                }
            });
        });
    }

    atualizarEtiquetas(informacoes: LoteMensagens) {
        runInAction(() => {
            const processosPorId = this.indexarProcessosPorId();

            Object.keys(informacoes).forEach(idProc => {
                const processo = processosPorId.get(parseInt(idProc));
                if (!processo) {
                    return;
                }
                processo.etiquetas = informacoes[idProc] as any as IEtiqueta[];
            });
        });
    }

    atualizarTarefaProcesso(informacoes: LoteMensagens) {
        runInAction(() => {
            const processosPorId = this.indexarProcessosPorId();

            Object.keys(informacoes).forEach(idProc => {
                const processo = processosPorId.get(parseInt(idProc));
                if (!processo) {
                    return;
                }
                processo.tarefaFluxoPje = informacoes[idProc] as any as ITarefaFluxoPje;
                processo.resetMostrar();
                FiltroStore.aplicarFiltros(processo);
                if (!processo.mostrar) {
                    processo.selecionar(false);
                }
            });
        });
    }

    atualizarCategoriaMinuta(informacoes: Map<number, MinutaCategoriaEnvelope[]>) {
        Object.keys(informacoes).forEach(idProc => {
            const idProcesso = parseInt(idProc);
            const minutaCategoriaEnvelopeArray = informacoes[idProcesso];
            const minutaCat0 = minutaCategoriaEnvelopeArray[0];
            if (!minutaCat0) {
                return;
            }
            const numMinuta = minutaCategoriaEnvelopeArray[0].id.numeroMinuta;
            const processo = ProcessoStore._processos.find(proc => proc.identificadorProcesso === idProcesso);
            if (!processo) {
                return
            }
            const minuta = processo?.minutas?.find(min => min.numero === numMinuta);
            if (!minuta) {
                return;
            }
            const categorias = minutaCategoriaEnvelopeArray.map(mce => {
                return CategoriaMinuta.novo(mce.categoriaDB);
            });

            runInAction(() => {
                minuta.categorias = categorias;
            });
        })
    }

    fechamentoDeMinuta(informacoes: Map<number, string>) {
        Object.keys(informacoes).forEach(numMinuta => {
            const validacaoMinuta = informacoes[numMinuta];
            ProcessoStore._processos.find(proc => {
                const minuta = proc.minutas.find(min => min.numero === parseInt(numMinuta));
                if (minuta) {
                    runInAction(() => {
                        minuta.semEmentaOuDecisao = validacaoMinuta;
                    });
                }
                return minuta !== undefined;
            });
        });
    }

    atualizarContemTrechoPubliquese(informacoes: Map<number, string>) {
        Object.keys(informacoes).forEach(numMinuta => {
            const contemTrechoPubliqueseMinuta = informacoes[numMinuta];
            ProcessoStore._processos.find(proc => {
                const minuta = proc.minutas.find(min => min.numero === parseInt(numMinuta));
                if (minuta) {
                    runInAction(() => {
                        minuta.contemTrechoPubliquese = contemTrechoPubliqueseMinuta;
                    });
                }
                return minuta !== undefined;
            });
        });
    }

    atualizarContemTrechoAposPauta(informacoes: Map<number, string>) {
        Object.keys(informacoes).forEach(numMinuta => {
            const contemTrechoAposPautaMinuta = informacoes[numMinuta];
            ProcessoStore._processos.find(proc => {
                const minuta = proc.minutas.find(min => min.numero === parseInt(numMinuta));
                if (minuta) {
                    runInAction(() => {
                        minuta.contemTrechoAposPauta = contemTrechoAposPautaMinuta;
                    });
                }
                return minuta !== undefined;
            });
        });
    }

    atualizarBackgroundProcesso(idProcesso, minutaAberta, usuario, _tipoMinuta) {
        idProcesso = idProcesso && parseInt(idProcesso[0]);
        minutaAberta = minutaAberta && minutaAberta[0];
        usuario = usuario && usuario[0];
        if (!usuario && usuario != UsuarioStore.login) {
            return;
        }

        ProcessoStore._processos.forEach(proc => {
            runInAction(() => {

                proc.background = "";
                if (proc.identificadorProcesso === idProcesso) {
                    proc.background = "#61D8FC";
                }
            })
        })

    }

    atualizarMinuta(_numMinuta: number) {

    }

    atualizarControleGabinete(numControleGabinete: number) {
        if (numControleGabinete) {
            ControleGabineteService.agendarAtualizacaoControles();
        }
    }

    minutaArquivada(alteracao: Map<number, number>) {
        Object.keys(alteracao).forEach(idProcesso => {
            const processo = ProcessoStore._processos.find(proc => proc.identificadorProcesso === parseInt(idProcesso));
            if (processo) {
                runInAction(() => {
                    const minutaArquivada = alteracao[idProcesso];
                    const selecionado = processo.minutas.find(minuta => minuta.numero === minutaArquivada)?.selecionada ?? false;
                    processo.minutas = processo.minutas.filter(minuta => minuta.numero !== minutaArquivada);
                    processo.selecionar(selecionado);
                });
            }
        })
    }

    remocaoComentario(alteracao: Map<number, number>) {
        Object.keys(alteracao).forEach(idProcessoString => {
            const idProcesso = parseInt(idProcessoString);
            const processo = ProcessoStore._processos.find(proc => proc.identificadorProcesso === idProcesso);
            if (processo) {
                const minutaSemComentario = alteracao[idProcesso];
                const minuta = processo.minutas.find(min => min.numero === minutaSemComentario.numMinuta);
                if (!minuta) {
                    return;
                }
                const indexComentarioDeletado = minuta.comentarios.findIndex(comentario => minutaSemComentario.numero === comentario.numero);
                runInAction(() => {
                    minuta.comentarios.splice(indexComentarioDeletado, 1);
                    if (minuta.comentarios.length == 0) {
                        processo.idsMinutasComentadas = processo.idsMinutasComentadas.filter(ids => ids !== minuta.numero);
                    }
                });
            }
        });

    }

    alteracaoComentario(alteracao: Map<number, Comentario>) {
        Object.keys(alteracao).forEach(idProcesso => {
            const processo = ProcessoStore._processos.find(proc => proc.identificadorProcesso === parseInt(idProcesso));
            if (processo) {
                const minutaComentario = alteracao[idProcesso];
                const minutaAlterada = processo.minutas.find(minuta => minuta.numero === minutaComentario.numMinuta);
                if (!minutaAlterada) {
                    return;
                }
                const comentarioModificado = minutaAlterada.comentarios.find(comentario => comentario.numero === minutaComentario.numero);
                const texto = minutaComentario.texto;
                if (comentarioModificado) {
                    runInAction(() => {
                        comentarioModificado.texto = texto;
                    });
                }
            }
        });
    }

    novoComentario(alteracao) {

        Object.keys(alteracao).forEach(idProcessoString => {
            const idProcesso = parseInt(idProcessoString);
            const processo = ProcessoStore._processos.find(proc => proc.identificadorProcesso === idProcesso);
            if (processo) {
                const minutaComentario = alteracao[idProcesso];
                const minutaAlterada = processo.minutas.find(minuta => minuta.numero === minutaComentario.numMinuta);
                if (!minutaAlterada) {
                    return;
                }
                runInAction(() => {
                    minutaAlterada.comentarios.splice(0, 0, minutaComentario);
                    processo.idsMinutasComentadas.push(minutaAlterada.numero);
                });
            }
        });
    }

    alteracaoMinutas(minutas: Minuta[]) {
        minutas.forEach(minutaDto => {
            const identificadorProcesso = (minutaDto as any)?.identificadorProcesso;
            if (!identificadorProcesso) {
                console.warn("Minuta sem identificadorProcesso:", minutaDto);
                return;
            }

            const processo = ProcessoStore._processos.find(proc => proc.identificadorProcesso === identificadorProcesso);
            if (!processo) {
                console.warn(`Processo não encontrado para identificadorProcesso: ${identificadorProcesso}`);
                return;
            }

            const minutaAlterada = Minuta.criar(minutaDto, processo);

            // Encontrar o índice da minuta a ser alterada, pelo tipo.codigo
            const indexMinutaAlterada = processo.minutas.findIndex(min => min.numero === minutaAlterada.numero);

            if (indexMinutaAlterada === -1) {
                console.warn(`Minuta não encontrada no processo ${identificadorProcesso} para tipo.codigo: ${minutaAlterada.tipo.codigo}`);
                return;
            }


            runInAction(() => {
                const minutaRetirada = processo.minutas.splice(indexMinutaAlterada, 1);

                if (minutaRetirada.length > 0) {
                    minutaAlterada.selecionada = minutaRetirada[0].selecionada;
                    minutaAlterada.comentarios = minutaRetirada[0].comentarios;
                    minutaAlterada.movimentosCNJ = minutaRetirada[0].movimentosCNJ;
                    minutaAlterada.mostrar = minutaRetirada[0].mostrar;
                }
                processo.minutas.push(minutaAlterada);
                if (minutaAlterada.tipo.codigo != TipoMinuta.VOTO && minutaAlterada.situacaoMinuta.codigo >= SituacaoMinuta.LIBERADA_PARA_ASSINATURA_OU_INCLUSAO_EM_PAUTA
                    && RouterStore.isEscaninhoAssinatura) {
                    processo.resetMostrar();
                    FiltroStore.aplicarFiltros(processo);
                }

                if(ProcessoExpandidoStore.idProcessoPainelExpandido == processo.numeroProcesso) {
                    const minutaExpandida = ProcessoExpandidoStore.minutas.find(min => min.numero === minutaAlterada.numero);
                    if(minutaExpandida){
                        minutaExpandida.textoFuncaoMagistrado = minutaAlterada.textoFuncaoMagistrado;
                    }
                }
            });
        });
    }


    async alteracaoTipoJulgamento(idsProcesso) {
        idsProcesso.forEach(idProc => {
            const processoAlterado = ProcessoStore._processos.find(proc => proc.identificadorProcesso === idProc);
            runInAction(() => {
                if (processoAlterado?.tipoJulgamento?.tipoJulgamento) {
                    processoAlterado.tipoJulgamento.tipoJulgamento = processoAlterado?.tipoJulgamento?.tipoJulgamento === TipoJulgamentoEnum.PRESENCIAL ? TipoJulgamentoEnum.VIRTUAL : TipoJulgamentoEnum.PRESENCIAL;
                }
            })
        });
    }

    votosLiberados(alteracao: Map<string, boolean>) {
        Object.keys(alteracao).forEach(idProcessoString => {
            const processo = ProcessoStore._processos.find(proc => proc.identificadorProcesso === parseInt(idProcessoString));
            if (processo) {
                const minutaAlterada = processo.minutas.find(min => min.tipo.codigo === TipoMinuta.VOTO);
                if (!minutaAlterada) {
                    return;
                }
                runInAction(() => {
                    minutaAlterada.votoJaliberado = alteracao[idProcessoString];
                });
            }
        });
    }

    alteracaoTriagemLivre(alteracao: Map<number, string>) {
        const idsProcesso = Object.keys(alteracao);

        idsProcesso.forEach(idProcesso => {
            const textoTriagemLivre = alteracao[idProcesso];
            const processo = ProcessoStore._processos.find(proc => proc.identificadorProcesso === parseInt(idProcesso));
            if (processo) {
                runInAction(() => {
                    processo.triagemLivre = textoTriagemLivre;
                });
            }
        });
    }

    alteracaoNovaMinuta(minuta: Minuta) {
        const processo = ProcessoStore._processos.find(proc => proc.identificadorProcesso === (minuta as any).identificadorProcesso);

        if (processo) {
            runInAction(() => {
                try {
                    const minutaNova = Minuta.criar(minuta, processo);
                    //minutaNova.mostrar = false;
                    processo.minutas = processo.minutas.concat(minutaNova);
                    FiltroStore.aplicarFiltros(processo);
                } catch (error) {
                    console.error("Erro ao receber minuta nova", error);
                }
            });
        }
    }

    alteracaoMinutaMovimento(minutaMovimento: MinutaMovimentos[]) {
        ProcessoStore._processos.find((processo) => {
            minutaMovimento.forEach(mm => {
                const minuta = processo.minutas.find(minuta => minuta.numero === mm.numMinuta);
                if (minuta) {
                    runInAction(() => {
                        minuta.movimentosCNJ = minutaMovimento.find(mm => mm.numMinuta === minuta.numero)?.movimentos || [];
                    });
                }
            })
        });
    }

    removerProcesso(idProcesso) {
        // Atualizações ainda na fila para um processo removido não podem trazê-lo de volta.
        Object.values(this.lotesPendentes).forEach(lote => {
            delete lote[idProcesso];
        });
        this.idsPendentes.delete(String(idProcesso));

        const indexParaExclusao = ProcessoStore._processos.findIndex(proc => proc.identificadorProcesso === idProcesso);
        if (indexParaExclusao < 0) {
            return;
        }
        runInAction(() => {
            ProcessoStore._processos.splice(indexParaExclusao, 1);
        });
    }

}

export default new ClienteEnvelopeService();
